import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Modal, Button, Spin, message } from 'antd';
import { AimOutlined, EditOutlined, BulbOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

const CORAL = '#D85A30';
const INK = '#2C2C2A';
const MUT = '#A08C82';

const fmt0 = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '0';
  const neg = n < 0;
  return (neg ? '-' : '') + Math.abs(n).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
const wan = (v) => (Number(v) / 10000).toFixed(2) + '万';
const fmtMonth = (m) => {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || '—';
  return `${m.slice(0, 4)}年${Number(m.slice(5, 7))}月`;
};

// 常驻「目标跟进」看板卡片：读 /api/earn-goal/progress，一眼看出完成/差距/时间/预计/建议
export default function EarnGoalCard() {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState(100000);
  const [startMonth, setStartMonth] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/earn-goal/progress').then(r => r.json());
      if (d.success) setP(d.data);
    } catch { /* 静默失败，卡片不打断看板 */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('earn-goal-changed', h);
    return () => window.removeEventListener('earn-goal-changed', h);
  }, [load]);

  const openEdit = () => {
    if (!p) return;
    setTarget(p.target || 100000);
    setStartMonth(p.startMonth || '');
    setNote((p.goal && p.goal.note) || '');
    setEditOpen(true);
  };

  const save = async () => {
    const t = Number(target);
    if (!t || t <= 0) { message.error('目标净利润需大于 0'); return; }
    if (!/^\d{4}-\d{2}$/.test(startMonth)) { message.error('请选择起点月份'); return; }
    setSaving(true);
    try {
      const r = await apiFetch('/api/earn-goal', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: t, startMonth, note }),
      }).then(x => x.json());
      if (r.success) {
        message.success('目标已保存');
        setEditOpen(false);
        window.dispatchEvent(new CustomEvent('earn-goal-changed'));
      } else message.error(r.error || '保存失败');
    } catch { message.error('保存失败'); }
    setSaving(false);
  };

  // 环形进度 SVG（封顶 100%）
  const ring = useMemo(() => {
    const pct = Math.min(100, Math.max(0, Number(p?.percent) || 0));
    const R = 56, C = 2 * Math.PI * R;
    return { pct, dash: (pct / 100) * C, C };
  }, [p]);

  if (loading) return <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="small" /></div>;

  const cardBox = { background: '#FFF8F4', border: '1px solid rgba(216,90,48,0.12)', borderRadius: 12, padding: '12px 14px' };

  // 没有账务流水数据
  if (!p || !p.hasData) {
    return (
      <div style={{ ...cardBox, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AimOutlined style={{ color: CORAL, fontSize: 18 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: INK }}>目标跟进</div>
            <div style={{ fontSize: 12, color: MUT, marginTop: 2 }}>暂无账务流水，请先在「财务管理 → 账务流水」导入账单后自动开启进度跟踪</div>
          </div>
        </div>
        <Button size="small" icon={<EditOutlined />} onClick={openEdit}>设定目标</Button>
        <EditModal open={editOpen} setOpen={setEditOpen} target={target} setTarget={setTarget} startMonth={startMonth} setStartMonth={setStartMonth} note={note} setNote={setNote} saving={saving} save={save} />
      </div>
    );
  }

  // 超额完成时颜色处理
  const done = Number(p.completed) >= Number(p.target);
  const bestSug = (p.suggestions && p.suggestions[0]) || null;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div style={{ ...cardBox }}>
      {/* 标题行 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AimOutlined style={{ color: CORAL, fontSize: 18 }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>目标跟进</span>
          {p.goal && p.goal.note ? <span style={{ fontSize: 12, color: MUT }}>· {p.goal.note}</span> : null}
        </div>
        <Button size="small" icon={<EditOutlined />} onClick={openEdit}>编辑目标</Button>
      </div>

      {/* 进度环 + 三数字 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
        <div style={{ position: 'relative', width: 132, height: 132, flexShrink: 0 }}>
          <svg width="132" height="132" viewBox="0 0 132 132" role="img" aria-label={`进度 ${ring.pct.toFixed(1)}%`}>
            <circle cx="66" cy="66" r="56" fill="none" stroke="#F3E2D8" strokeWidth="12" />
            <circle cx="66" cy="66" r="56" fill="none" stroke={done ? '#639922' : CORAL} strokeWidth="12"
              strokeLinecap="round" strokeDasharray={`${ring.dash} ${ring.C}`} transform="rotate(-90 66 66)" />
            <text x="66" y="62" textAnchor="middle" fontSize="24" fontWeight="700" fill={done ? '#639922' : CORAL} fontFamily="DIN Alternate, 'Segoe UI', sans-serif">{ring.pct.toFixed(1)}%</text>
            <text x="66" y="84" textAnchor="middle" fontSize="11" fill={MUT}>{done ? '已达成' : '已完成'}</text>
          </svg>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, flex: '1 1 320px' }}>
          {[
            { k: '已完成', v: '¥' + fmt0(p.completed), c: done ? '#639922' : CORAL },
            { k: '还差', v: done ? '¥0' : '¥' + fmt0(p.remaining), c: INK },
            { k: '目标', v: '¥' + fmt0(p.target), c: INK },
          ].map((x) => (
            <div key={x.k} style={{ background: '#fff', border: '1px solid rgba(216,90,48,0.10)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, color: MUT }}>{x.k}</div>
              <div style={{ fontSize: 19, fontWeight: 700, color: x.c, marginTop: 3, fontFamily: "DIN Alternate, 'Segoe UI', sans-serif" }}>{x.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 时间轴 */}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: MUT, marginBottom: 4 }}>
          <span>时间轴</span>
          <span style={{ color: INK, fontWeight: 600 }}>{fmtMonth(p.startMonth)}</span>
          <span>→ 今天 {todayStr}</span>
          {p.etaMonth ? <span style={{ color: CORAL, fontWeight: 600 }}>→ 预计 {fmtMonth(p.etaMonth)} 达成</span> : <span>→ 待累计</span>}
        </div>
        <div style={{ position: 'relative', height: 8, borderRadius: 4, background: '#F3E2D8', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, Math.max(0, ring.pct))}%`, background: done ? '#639922' : CORAL, borderRadius: 4 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: MUT, marginTop: 5 }}>
          <span>月均净利 ¥{fmt0(p.avgMonthly)}{p.foldNote ? `（${p.foldNote}）` : ''}</span>
          <span>{done ? '目标已达成' : (p.monthsLeft != null ? `按当前速度还需 ${p.monthsLeft} 个月` : '')}</span>
        </div>
      </div>

      {/* 优化建议（取最有效一条） */}
      {bestSug && !done && (
        <div style={{ marginTop: 12, background: '#FFF0E9', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <BulbOutlined style={{ color: '#BA7517' }} />
          <span style={{ fontSize: 13, color: INK }}>
            <span style={{ fontWeight: 600 }}>{bestSug.title}</span>
            <span style={{ color: MUT }}>（{bestSug.desc}）</span>：每月多赚 <span style={{ color: CORAL, fontWeight: 700 }}>¥{fmt0(bestSug.saving)}</span>
            {bestSug.monthsLeft != null ? <>，缩到 <span style={{ fontWeight: 700, color: INK }}>{bestSug.monthsLeft} 个月</span></> : null}
          </span>
        </div>
      )}

      <EditModal open={editOpen} setOpen={setEditOpen} target={target} setTarget={setTarget} startMonth={startMonth} setStartMonth={setStartMonth} note={note} setNote={setNote} saving={saving} save={save} />
    </div>
  );
}

function EditModal({ open, setOpen, target, setTarget, startMonth, setStartMonth, note, setNote, saving, save }) {
  return (
    <Modal open={open} onCancel={() => setOpen(false)} onOk={save} confirmLoading={saving} okText="保存" cancelText="取消" width={420}
      title={<span style={{ fontSize: 15, fontWeight: 600, color: INK }}><AimOutlined style={{ color: CORAL, marginRight: 8 }} />设定目标净利润</span>}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: INK, marginBottom: 6 }}>目标净利润（元）</div>
        <input type="number" min="0" step="10000" value={target}
          onChange={(e) => setTarget(e.target.value)}
          style={{ width: '100%', height: 36, border: '1.5px solid #D85A30', borderRadius: 8, padding: '0 12px', fontSize: 15, fontWeight: 600, color: INK, boxSizing: 'border-box' }} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: INK, marginBottom: 6 }}>起点月份（累计净利从该月起算）</div>
        <input type="month" value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          style={{ width: '100%', height: 36, border: '1.5px solid #D5CBC3', borderRadius: 8, padding: '0 12px', fontSize: 14, color: INK, boxSizing: 'border-box' }} />
      </div>
      <div>
        <div style={{ fontSize: 13, color: INK, marginBottom: 6 }}>备注（可选）</div>
        <input type="text" value={note} maxLength={40} placeholder="如：年度目标 / 回本目标"
          onChange={(e) => setNote(e.target.value)}
          style={{ width: '100%', height: 36, border: '1.5px solid #D5CBC3', borderRadius: 8, padding: '0 12px', fontSize: 14, color: INK, boxSizing: 'border-box' }} />
      </div>
      <div style={{ fontSize: 11, color: MUT, marginTop: 12, lineHeight: 1.6 }}>
        口径：已完成 = 从起点月累计到当前月的全渠道净利（拼多多到账 + 微信发货）；数字随公司看板每次刷新自动更新，小李（AI 财务）同步读取此目标。
      </div>
    </Modal>
  );
}
